import { prepareSyncRequest } from "@storytell/auth";
import { type Auth, useAuthClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import type { Named } from "@storytell/logger";
import { newErrorResult, newSuccessResult, type Result } from "@storytell/result";
import type { User } from "firebase/auth";

export const syncAuthWithAPI = async (deps: {
  logger: Named;
  firebaseUser: User;
  deviceId: string;
  locale: string;
  userAgent: string;
  token: string;
}): Promise<
  Result<{
    authenticatedUser: Auth.AuthenticatedUser;
    token: string;
  }>
> => {
  const { logger, firebaseUser, deviceId, locale, userAgent, token } = deps;

  const syncResponse = await useAuthClient().sync(
    prepareSyncRequest(firebaseUser, deviceId, locale, userAgent),
    useCallOptions(withBearerToken(token))
  );

  if (syncResponse.isErr()) {
    logger.warn("auth sync failed", { error: syncResponse.error });
    return newErrorResult(syncResponse.error.code, syncResponse.error.message);
  }

  let finalToken = token;
  if (syncResponse.value.tokenRefreshReason) {
    try {
      finalToken = await firebaseUser.getIdToken(true);
    } catch (error) {
      logger.warn("auth sync token refresh failed", { error: error });
      return newErrorResult(500, "Failed to refresh the authentication token");
    }
  }
  logger.info("auth sync complete", { response: syncResponse.value });

  // Validate required realm fields
  const requiredFields = [
    { key: "projectId", name: "projectId" },
    { key: "organizationId", name: "organizationId" },
    { key: "tenantId", name: "tenantId" },
    { key: "workspaceId", name: "workspaceId" },
  ] as const;

  for (const field of requiredFields) {
    if (!syncResponse.value.authenticatedUser.realm?.[field.key]) {
      logger.warn(`auth sync missing ${field.name}`, {
        authenticatedUser: syncResponse.value.authenticatedUser,
      });
      return newErrorResult(400, `Authenticated user does not have a ${field.name}`);
    }
  }

  return newSuccessResult({
    token: finalToken,
    authenticatedUser: syncResponse.value.authenticatedUser,
  });
};
